import React from 'react';
import { useContext, useEffect, useState } from 'react';
import { Row, Col, Button, Form, Alert, Card, Table, Modal } from 'react-bootstrap';

import StateContext from '../../../context/State';
import PortalService from '../../../services/portal.service';
import ScheduleSigning from './ScheduleSigning';
import getArrayBufferFromFile from '../../../../../util/getArrayBufferFromFile';
import getIconForFileType from '../../../../../util/getIconForFileType';
import { convertFrom24To12Format } from '../../../../../util/genericDateAndTimeFuncs';
import { DefaultDocumentResponse } from '../../../data';
import { DocumentsFromLawyerResponse, FileUploadModel, PortalModel } from '../../../types';


const ConfigureSigning = () => {

    const { state } = useContext(StateContext);
    const portalService = new PortalService(state);

    const [province, setProvince] = useState<string>(null);
    const [sessionId, setSessionId] = useState<string>(null);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [scheduled, setScheduled] = useState<boolean>(false);
    const [scheduledTime, setScheduledTime] = useState<string>('');
    const [portalData, setPortalData] = useState<PortalModel>(null);
    const [attentionRequired, setAttentionRequired] = useState<boolean>(false);
    const [fileToBeUploaded, setFileToBeUploaded] = useState<FileUploadModel>(null);
    const [remoteSigningDocuments, setRemoteSigningDocuments] = useState<DocumentsFromLawyerResponse>(DefaultDocumentResponse);
    const [remoteSigningDocsNames, setRemoteSigningDocsNames] = useState<string []>([]);
    const [defaultRemoteSigningDocuments, setDefaultRemoteSigningDocuments] = useState<DocumentsFromLawyerResponse>(DefaultDocumentResponse);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [selectedDocument, setSelectedDocument] = useState('');


    useEffect(() => {
        getRemoteSigningDocuments();
        getRemoteSigningSchedule();
        getDefaultRSPFiles();
        setScheduleandPortalData();
        const sessionId: string = portalService.getSessionId();
        const province: string = portalService.getProvince();
        setSessionId(sessionId);
        setProvince(province);
        // eslint-disable-next-line
    }, []);

    const getRemoteSigningSchedule = async () => {
        try {
            const scheduleResponse = await portalService.joinRemoteSigningPortal(state.portalId.toString())
            if (scheduleResponse.scheduledEndTime && scheduleResponse.scheduledStartTime) {
                const scheduledTime = `${new Date(scheduleResponse.scheduledStartTime).toLocaleDateString()} from ${convertFrom24To12Format(scheduleResponse.scheduledStartTime)} to ${convertFrom24To12Format(scheduleResponse.scheduledEndTime)}`
                setScheduledTime(scheduledTime)
            }
        } catch (e) {
            console.log('unable to get schedule: ', e)
        }
    }

    // To Do
    const getRemoteSigningDocuments = async (): Promise<void> => {
        try {
            const remoteSigningDocumentsResponse = await portalService.getRemoteSigningDocuments(state.portalId.toString());

            if (remoteSigningDocumentsResponse) { 
                setRemoteSigningDocuments(remoteSigningDocumentsResponse);
                setRemoteSigningDocsNames(remoteSigningDocumentsResponse.Docs.map(d => d.Name.split('/').at(-1)));
            }
        } catch (e) {
            console.log('Unable to retreive shared documents: ', e)
        }
    }

    const getDefaultRSPFiles = async () => {
        try {
            const defaultRSPFilesResponse = await portalService.getDefaultRemoteSigningFiles(state.portalId.toString())
            if (defaultRSPFilesResponse) { setDefaultRemoteSigningDocuments(defaultRSPFilesResponse) }
        } catch (e) {
            console.log('unable to get default rsp files: ', e)
        }
    }

    const handleDocumentClicked = async (docName) => {
        try {
            await portalService.getRemoteSigningDocument(state.portalId.toString(), docName);
            //window.open(documentResponse)
        } catch (e) {
            alert(e);
        }
    }

    const handleDocumentDelete = (docName: string) => {
        setSelectedDocument(docName);
        setShowDeleteModal(true);
    };
    const handleConfirmDelete = (docName: string) => {
        confirmDocumentDelete(docName)
        setShowDeleteModal(false);
    };
    const handleCancelDelete = () => {
        setShowDeleteModal(false);
    };

    const confirmDocumentDelete = async (docName: string): Promise<void> => {
        try {
            const resp = await portalService.deleteRemoteSigningDocumentFromLawyer(state.portalId.toString(), docName);
            if (resp.data.success){
                getRemoteSigningDocuments();
            }
            else {
                alert(resp.data.code)
            }
        } catch (e) {
            if (e) {
                alert('Could not delete document. Please refresh the page and try again.')
            }
        }
    }

    const handleDefaultDocumentClicked = async (docName) => {
        try {
            await portalService.getDefaultRemoteSigningDocument(state.portalId.toString(), docName)
        } catch (e) {
            console.log('unable to get requested default document: ', e)
        }
    }

    const handleFileUploadComponent = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const files = Array.from(e.target.files);
        let uploadedFile: File;
        if (files) {
            if (files[0]) {
                uploadedFile = files[0];
                const fileName: string = uploadedFile.name;
                setFileToBeUploaded({ uploadedFile, fileName });
            }
        }
    }

    const handleUploadDocument = async (): Promise<void> => {

        if (!fileToBeUploaded) {
            alert('You must choose a file before submitting.');
            return;
        }

        if (fileToBeUploaded.uploadedFile.type === 'application/pdf') {
            try {
                const arrayBuffer: any = await getArrayBufferFromFile(fileToBeUploaded.uploadedFile);
                await portalService.uploadDocument(arrayBuffer, state.portalId, state.appId, fileToBeUploaded.fileName, 'RSP');
                getRemoteSigningDocuments();
            } catch (e) {
                console.log(e);
            }
        } else {
            alert('All documents uploaded to the remote signing session must be in .pdf format.')
        }
    }

    const handleAddToRemoteSigningDocuments = async (document) => {
        try {
            await portalService.addDefaultDocumentToRemoteSigning(state.portalId.toString(), document)
        } catch (e) {
            console.log('unable to add default documents to remote signing: ', e)
        }
        getRemoteSigningDocuments();
    }

    const getRemoteSigningRequestFromClient = async () => {
        const clientRSRequest = await portalService.getRemoteSigningRequestFromClient(state.portalId);
        if (clientRSRequest) { setAttentionRequired(true) }
    }

    const setScheduleandPortalData = () => {
        getRemoteSigningRequestFromClient();
        portalService.getPortalById(state.portalId)
            .then((portal: PortalModel[]) => {
                if (portal[0].RSPActive) {
                    setScheduled(true);
                    setPortalData(portal[0]);
                }
            })
            .catch(err => { console.log(err) })
    }

    const handleAddToCalendar = (startDate: string, endDate: string) => {
        const location = 'Remote Signing Portal';
        const details = 'Details for the Lawyer go here.';
        const title = 'LDD Remote Signing Session';

        const startDateRaw = new Date(startDate);
        let sd = startDateRaw.toISOString();
        sd = sd.replaceAll('-', '');
        sd = sd.replaceAll(':', '');
        sd = sd.replaceAll('.000', '');
        sd = sd.replaceAll('Z', '');

        const endDateRaw = new Date(endDate);
        let ed = endDateRaw.toISOString();
        ed = ed.replaceAll('-', '');
        ed = ed.replaceAll(':', '');
        ed = ed.replaceAll('.000', '');
        ed = ed.replaceAll('Z', '');

        window.open(`https://calendar.google.com/calendar/u/0/r/eventedit?text=${title}&dates=${sd}/${ed}&details=${details}&location=${location}`)
        // Reference: https://github.com/InteractionDesignFoundation/add-event-to-calendar-docs/blob/main/services/google.md
    }

    const filterNotInRemoteDocNames = (d) => {
        const docName = (d.Name).split('/').at(-1);
        return !remoteSigningDocsNames.includes(docName);
    }

    return (
        <>
            <Card>
                <Card.Header>
                    <Card.Title>Remote Closing Session</Card.Title>
                </Card.Header>

                <Card.Body>
                    <div>
                        <Row>
                            <Col>
                                <h4>Documents shared with Client</h4>
                                {
                                    defaultRemoteSigningDocuments.Docs.filter(filterNotInRemoteDocNames).length === 0 ?
                                        <>
                                            <Alert>No documents added.</Alert>
                                        </>
                                        :
                                        <Table striped hover>
                                            <tbody>
                                                {
                                                    defaultRemoteSigningDocuments.Docs.filter(filterNotInRemoteDocNames).map((doc, index) => {
                                                        const tempArray = (doc.Name).split("/");
                                                        const className = getIconForFileType(tempArray.at(-1));

                                                        return (
                                                            <tr key={'default-remote-signing-document-' + index}>
                                                                <Row>
                                                                    <Col xs={10}>
                                                                        <td
                                                                            onClick={() => { handleDefaultDocumentClicked(tempArray.at(-1)) }}
                                                                            key={index + 'link'}
                                                                        >
                                                                            <i
                                                                                className={className}
                                                                                style={{ padding: 5 }}
                                                                                key={index + 'icon'}
                                                                            />
                                                                            {tempArray.at(-1)}
                                                                        </td>
                                                                    </Col>


                                                                    <Col
                                                                        style={{ color: 'green', cursor: 'pointer' }}
                                                                        onClick={() => { handleAddToRemoteSigningDocuments(doc); }}
                                                                    >
                                                                        + Add
                                                                    </Col>
                                                                </Row>
                                                            </tr>
                                                        )
                                                    })
                                                }
                                            </tbody>
                                        </Table>
                                }

                                <br />

                                <Form.Group>
                                    <Form.Label>Upload Document</Form.Label>
                                    <Form.Control
                                        className="form-upload"
                                        type="file"
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => { handleFileUploadComponent(e) }}
                                    />
                                </Form.Group>

                                <Button onClick={() => handleUploadDocument()}>Upload</Button>

                                <br />
                                <br />
                                <h4>Documents in this session</h4>
                                {remoteSigningDocuments.Docs.length !== 0 ?
                                    <Table striped hover>
                                        <tbody>
                                            {
                                                remoteSigningDocuments.Docs.map((doc, index) => {
                                                    const tempArray = (doc.Name).split("/");
                                                    const className = getIconForFileType(tempArray.at(-1));
                                                    return (
                                                        <tr key={index + '-remote-signing-document'}>
                                                            <td
                                                                className="link"
                                                                onClick={() => { handleDocumentClicked(tempArray.at(-1)) }}
                                                                key={index + 'link'}

                                                            >
                                                                <i
                                                                    className={className}
                                                                    style={{ padding: 5 }}
                                                                    key={index + 'icon'}
                                                                />
                                                                {tempArray.at(-1)}
                                                            </td>
                                                            <td 
                                                                className="text-right"
                                                            >
                                                                <i
                                                                    onClick={ () => handleDocumentDelete(tempArray.at(-1)) } 
                                                                    className="fa-regular fa-trash-can">    
                                                                </i>
                                                            </td>
                                                        </tr>
                                                    )
                                                })

                                            }
                                        </tbody>
                                    </Table>
                                    :
                                    <Alert>No documents added.</Alert>
                                }
                            </Col>

                            <Col>
                                <h4>Session Details</h4>
                                {!scheduled &&
                                    <>
                                        {attentionRequired && <Alert variant='warning'>Client requested a Remote Closing Session</Alert>}
                                    </>
                                }
                                <Alert>Scheduled: {scheduledTime !== '' ? scheduledTime : 'Pending'}</Alert>

                                <Button
                                    variant={scheduled ? "outline-primary" : "primary"}
                                    className="mr-1"
                                    onClick={() => setShowModal(true)}
                                >
                                    {scheduled ? "Reschedule Session" : "Schedule Session"}
                                </Button>

                                <ScheduleSigning
                                    setScheduledTime={setScheduledTime}
                                    getRemoteSigningSchedule={getRemoteSigningSchedule}
                                    showModal={showModal}
                                    onHideModal={() => {
                                        setShowModal(false)
                                        setScheduleandPortalData()
                                    }}
                                />

                                <Button
                                    variant='outline-primary'
                                    disabled={!scheduled}
                                    onClick={() => {
                                        handleAddToCalendar(portalData.RSPScheduledStartTime, portalData.RSPScheduledEndTime)
                                    }}>
                                    Add to Calendar
                                </Button>

                                <Button
                                    disabled={!scheduled}
                                    onClick={() =>
                                        window.open('/' + process.env.REACT_APP_CLIENT_PORTAL_FRONTEND + '/joinRSLawyer' +
                                            '?SessionID=' + sessionId +
                                            '&province=' + province +
                                            '&appId=' + state.appId)
                                    }
                                >
                                    Join Session
                                </Button>

                            </Col>
                        </Row>
                    </div>
                </Card.Body>

            </Card>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Document</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    "{selectedDocument}" will no longer be shared with the client. Your client may have already downloaded this document. Are you sure you would like to remove it from ClientConnect?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCancelDelete}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={() => handleConfirmDelete(selectedDocument)}>
                        Confirm
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default ConfigureSigning;
